import { DrawingsSchema } from './generated/drawings/drawings-schema'
import { SandboxSchema } from './generated/sandbox/sandbox-schema'
import type { StructuredToolDefinition } from './types'

/**
 * Sandbox machine profiles. The runtime half of each profile (ops, grants,
 * documents, lifetime) lives in the api-service sandbox module.
 */
export type SandboxProfile =
  | 'ingest_lead'
  | 'ingest_parser'
  | 'document_search'
  | 'tabular_builder'
  | 'tabular_replay'
  | 'conversation'

/** The script environment a profile's machine runs in. */
export type SandboxEnvironment = 'documents' | 'data'

/** A named group of model-facing sandbox tools. Profiles and palettes name bundles, not tools. */
export type SandboxToolBundle = 'workspace' | 'drawings' | 'figures'

export const SANDBOX_TOOL_BUNDLES: Readonly<Record<SandboxToolBundle, readonly string[]>> = {
  // A shell and an editor over /workspace; nothing in it leaves the machine.
  // Same order as sandbox.json so palettes built from it keep their tool order.
  workspace: ['run_script', 'write_file', 'read_file', 'view_page_image', 'search_files'],
  // Brings a drawing in and publishes a crop out.
  drawings: ['locate_in_document', 'publish_figure'],
  // Publishes a plot without bringing a drawing in.
  figures: ['publish_figure']
}

const SANDBOX_TOOL_DEFINITIONS = new Map<string, StructuredToolDefinition>(
  ([...SandboxSchema, ...DrawingsSchema] as StructuredToolDefinition[]).map((tool) => [
    tool.toolSpec.name,
    tool
  ])
)

// Fail at import time, so a renamed tool breaks every suite that builds a palette.
for (const [bundle, names] of Object.entries(SANDBOX_TOOL_BUNDLES)) {
  for (const name of names) {
    if (!SANDBOX_TOOL_DEFINITIONS.has(name)) {
      throw new Error(`Sandbox bundle "${bundle}" names unknown tool "${name}"`)
    }
  }
}

/** Flat tool-name list for the given bundles, in bundle order, deduped. */
export function sandboxBundleToolNames(bundles: readonly SandboxToolBundle[]): string[] {
  return [...new Set(bundles.flatMap((bundle) => SANDBOX_TOOL_BUNDLES[bundle]))]
}

/** Tool definitions for the given bundles, in bundle order, deduped by name. */
export function sandboxBundleToolDefinitions(
  bundles: readonly SandboxToolBundle[]
): StructuredToolDefinition[] {
  return sandboxBundleToolNames(bundles).map((name) => {
    const definition = SANDBOX_TOOL_DEFINITIONS.get(name)
    if (!definition) throw new Error(`Unknown sandbox tool "${name}"`)
    return definition
  })
}
